import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import "./Survey.css";
import * as _ from "lodash";
import { Loader } from "rsuite";

export default function Survey() {
  const [surveyData, setSurveyData] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const availableMessages = {
    success: "Your survey was submitted succesfully! Redirecting...",
    successNoRedirect: "Your survey was submitted succesfully!",
    alreadySubmitted: "Survey arleady submitted.",
    expired: "Thank you for your interest, this survey is now closed.",
    wrongUrl: "Url is invalid.",
    genericFailure: "An error occured. Please try again later.",
    missingRequiredFields: "Please fill out all required fields.",
  };

  const searchParams = new URLSearchParams(window.location.search);
  const [theme, setTheme] = useState();
  const [missingKeys, setMissingKeys] = useState(new Set());

  const getLowercaseParamValue = (targetKey) => {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      if (key.toLowerCase() === targetKey.toLowerCase()) {
        return value.toLowerCase();
      }
    }
    return null;
  };
  useEffect(() => {
    axios
      .get("/api/Survey/GetSectionsAndQuestions", {
        params: {
          acid: parseInt(getLowercaseParamValue("acid"), 10) || null,
          programId: parseInt(searchParams.get("programId"), 10) || null,
          surveyCode: searchParams.get("surveyCode") || null,
          sentDate: searchParams.get("sentDate")
            ? new Date(searchParams.get("sentDate"))
            : null,
          eventCode: searchParams.get("eventCode") || null,
          hotelCode: searchParams.get("hotelCode") || null,
        },
      })
      .then((response) => {
        setSurveyData(response.data);
        setLoading(false);
        setMessage(response.data.errorMessage);
        if (response.data.errorCode === 200) {
          setMessageType("success");
        } else {
          setMessageType("error");
          switch (response.data.errorCode) {
            case 400:
              setMessage(availableMessages.wrongUrl);
              break;
            case 401:
              setMessage(availableMessages.alreadySubmitted);
              break;
            case 440:
              setMessage(availableMessages.expired);
              break;
            case 500:
              setMessage(availableMessages.genericFailure);
              break;
          }
        }
      })
      .catch((error) => {
        console.error("Failed to fetch survey data:", error);
        setMessageType("error");
        setMessage(availableMessages.genericFailure);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    axios
      .get("/api/init/getLoyaltyInfo", {
        params: {
          programLabel: surveyData?.programLabel,
        },
      })
      .then((response) => {
        setTheme(response.data.portalInfo); // flatten now
      })
      .catch((error) => {
        console.error("Failed to fetch survey data:", error);
      });
  }, [surveyData]);

  const handleSubmit = () => {
    if (!surveyData) return;

    const missing = new Set();

    surveyData.sectNewList.forEach((section, sectionIndex) => {
      section.questions.forEach((question, questionIndex) => {
        const key = `${sectionIndex}_${questionIndex}`;
        const questionIsMandatory = question.questionIsMandatory;

        const value = responses[key]?.value;

        if (
          questionIsMandatory &&
          (value === undefined || value === null || value === "")
        ) {
          missing.add(key);
        }
      });
    });

    if (missing.size > 0) {
      setMissingKeys(missing); // highlight those
      const firstMissing = [...missing][0];
      const el = document.getElementById(`q_${firstMissing}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });

      setMessage(availableMessages.missingRequiredFields);
      setMessageType("error");
      return;
    }

    const answers = Object.values(responses).map((r) => ({
      Answer_Value: r.value,
      FK_Survey_Question: r.questionId,
    }));

    const reply = {
      ProgramLabel: surveyData.programLabel,
      Acid: surveyData.acid,
      AC_SourceID: "",
      SurveyCode: surveyData.surveyCode,
      Event_Code: searchParams.get("eventCode"),
      HotelCode: searchParams.get("hotelCode"),
      Answers: answers,
    };

    function isValidUrl(str) {
      if (typeof str !== "string" || !str.trim()) return false; // null, undefined, empty, non-string

      try {
        // If no scheme present, assume http://
        const withScheme = str.includes("://") ? str : "http://" + str;
        new URL(withScheme);
        return true;
      } catch {
        return false;
      }
    }

    setSubmitting(true);

    axios
      .post("/api/Survey/PostSurveyAnswer", { reply })
      .then((response) => {
        setLoading(true);
        window.scrollTo(0, 0);
        if (response.data.errorCode === 200) {
          setMessageType("success");
          if (isValidUrl(surveyData.surveyRedirectPage)) {
            setMessage(availableMessages.success);
            setTimeout(function () {
              window.location.replace(surveyData.surveyRedirectPage);
            }, 3000);
          } else {
            setMessage(availableMessages.successNoRedirect);
          }
        } else {
          //throw error
          throw new Error(response.data.errorCode);
        }
      })
      .catch((error) => {
        setMessageType("error");
        console.error("Submission failed:", error.message);
        switch (error.message) {
          case "400":
            setMessage(availableMessages.wrongUrl);
            break;
          case "401":
            setMessage(availableMessages.alreadySubmitted);
            break;
          case "500":
            setMessage(availableMessages.genericFailure);
            break;
          default:
            setMessage(availableMessages.genericFailure);
            break;
        }
        setMessageType("error");
      });
  };

  const handleChange = (sectionIndex, questionIndex, value) => {
    const question =
      surveyData.sectNewList[sectionIndex].questions[questionIndex];
    const key = `${sectionIndex}_${questionIndex}`;
    setResponses((prev) => ({
      ...prev,
      [key]: { value, questionId: question.questionId },
    }));
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {}, 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (loading || !surveyData || !theme)
    return (
      <>
        {messageType === "error" || messageType === "success" ? (
          <div className=" text-center text-2xl" style={{ marginTop: "20%" }}>
            {message}{" "}
          </div>
        ) : (
          <div className=" text-center text-2xl" style={{ marginTop: "20%" }}>
            Loading survey... Please wait!
          </div>
        )}
      </>
    );

  return (
    <>
      {message && (
        <div
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            padding: "1rem 1.5rem",
            borderRadius: "8px",
            color: messageType === "success" ? "#0bcf04" : "red",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1000,
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          {message}
        </div>
      )}

      <div className="survey-container">
        <div
          style={{
            backgroundImage: `url(https://verity.blob.core.windows.net/portal/${surveyData?.programLabel}/logo.svg)`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "auto 112px",
          }}
        ></div>
        <h1
          className="survey-title"
          style={{
            color: theme?.buttonBackgroundColor,
          }}
        >
          Satisfactory Survey
        </h1>
        <h5
          className="survey-title"
          style={{
            color: theme?.buttonBackgroundColor,
          }}
        >
          Thank you for spending a couple of minutes to share your experience
          with us{" "}
        </h5>

        {surveyData?.sectNewList?.map((section, sectionIndex) => (
          <div key={sectionIndex} className="survey-section">
            <h2 className="section-title">{section.sectionDescription}</h2>

            {section.questions
              .sort((a, b) => a.questionDisplayOrder - b.questionDisplayOrder)
              .map((question, questionIndex) => {
                const key = `${sectionIndex}_${questionIndex}`;
                const responseValue = responses[key]?.value || "";

                switch (question.questionType) {
                  case "Score":
                    const lovValues = question.questionLOV?.lovValues || [];
                    const maxScore = question.questionMaxScore || 5;

                    // Create a full array from 1 to maxScore
                    const scoreOptions = Array.from(
                      { length: maxScore },
                      (_, i) => {
                        const scoreValue = (i + 1).toString();
                        const matchingLov = lovValues.find(
                          (val) => val.lovAnswerValue === scoreValue
                        );
                        return {
                          value: scoreValue,
                          label: matchingLov?.lovAnswerLabel || null,
                        };
                      }
                    );

                    const selectedLabel =
                      scoreOptions.find((opt) => opt.value === responseValue)
                        ?.label || "";

                    return (
                      <div
                        key={questionIndex}
                        id={`q_${sectionIndex}_${questionIndex}`}
                        className={`survey-question ${
                          missingKeys.has(`${sectionIndex}_${questionIndex}`)
                            ? "missing"
                            : ""
                        }`}
                      >
                        <p className="question-text underline">
                          {question.questionDescr}
                          {question.questionIsMandatory && (
                            <span style={{ color: "red" }}> *</span>
                          )}
                        </p>

                        <div
                          className="survey-options"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1rem",
                          }}
                        >
                          <div>
                            {scoreOptions.map((item, i) => (
                              <label key={i} className="radio-option">
                                <input
                                  type="radio"
                                  name={key}
                                  value={item.value}
                                  checked={responseValue === item.value}
                                  onChange={() =>
                                    handleChange(
                                      sectionIndex,
                                      questionIndex,
                                      item.value
                                    )
                                  }
                                  style={
                                    theme ? { color: theme.buttonColor } : {}
                                  }
                                />
                                <br />
                                <span>{item.value}</span>
                              </label>
                            ))}
                          </div>

                          {!question.questionIsNPS && selectedLabel && (
                            <span
                              style={{
                                fontWeight: "bold",
                                color: theme?.buttonColor || "#333",
                              }}
                            >
                              {selectedLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  case "LOV":
                    return (
                      <div
                        key={questionIndex}
                        id={`q_${sectionIndex}_${questionIndex}`}
                        className={`survey-question ${
                          missingKeys.has(`${sectionIndex}_${questionIndex}`)
                            ? "missing"
                            : ""
                        }`}
                      >
                        <p className="question-text underline">
                          {question.questionDescr}
                          {question.questionIsMandatory && (
                            <span style={{ color: "red" }}> *</span>
                          )}
                        </p>

                        <select
                          className="lov-select custom-select-theme"
                          value={responseValue}
                          onChange={(e) =>
                            handleChange(
                              sectionIndex,
                              questionIndex,
                              e.target.value
                            )
                          }
                          style={{
                            borderColor: theme?.buttonColor,
                            color: theme?.buttonBackgroundColor,
                          }}
                        >
                          <option value="">Select an option</option>

                          {question.questionLOV?.lovValues
                            ?.sort(
                              (a, b) => a.lovDisplayOrder - b.lovDisplayOrder
                            )
                            .map((lovItem) => (
                              <option
                                key={lovItem.lovValueId}
                                value={lovItem.lovAnswerValue}
                              >
                                {lovItem.lovAnswerLabel}
                              </option>
                            ))}
                        </select>
                      </div>
                    );
                  case "Text":
                    return (
                      <div
                        key={questionIndex}
                        id={`q_${sectionIndex}_${questionIndex}`}
                        className={`survey-question ${
                          missingKeys.has(`${sectionIndex}_${questionIndex}`)
                            ? "missing"
                            : ""
                        }`}
                      >
                        <p className="question-text underline">
                          {question.questionDescr}
                          {question.questionIsMandatory && (
                            <span style={{ color: "red" }}> *</span>
                          )}
                        </p>
                        <textarea
                          className="survey-textarea"
                          rows="4"
                          value={responseValue}
                          onChange={(e) =>
                            handleChange(
                              sectionIndex,
                              questionIndex,
                              e.target.value
                            )
                          }
                          style={{
                            borderColor: theme?.buttonColor, // backup
                          }}
                        />
                      </div>
                    );
                  case "Boolean":
                    return (
                      <div
                        key={questionIndex}
                        id={`q_${sectionIndex}_${questionIndex}`}
                        className={`survey-question ${
                          missingKeys.has(`${sectionIndex}_${questionIndex}`)
                            ? "missing"
                            : ""
                        }`}
                      >
                        <p className="question-text underline">
                          {question.questionDescr}
                          {question.questionIsMandatory && (
                            <span style={{ color: "red" }}> *</span>
                          )}
                        </p>
                        <div className="survey-options boolean">
                          <label className="radio-option">
                            <input
                              type="radio"
                              className="boolean"
                              name={key}
                              value="true"
                              checked={responseValue === "true"}
                              onChange={() =>
                                handleChange(
                                  sectionIndex,
                                  questionIndex,
                                  "true"
                                )
                              }
                              style={{
                                color: theme?.buttonColor, // border color
                                borderColor: theme?.buttonColor, // backup
                              }}
                            />
                            <br />
                            <span>Yes</span>
                          </label>
                          <label className="radio-option">
                            <input
                              type="radio"
                              className="boolean"
                              name={key}
                              value="false"
                              checked={responseValue === "false"}
                              onChange={() =>
                                handleChange(
                                  sectionIndex,
                                  questionIndex,
                                  "false"
                                )
                              }
                              style={{
                                color: theme?.buttonColor, // border color
                                borderColor: theme?.buttonColor, // backup
                              }}
                            />
                            <br />
                            <span>No</span>
                          </label>
                        </div>
                      </div>
                    );
                  default:
                    return (
                      <div
                        key={questionIndex}
                        className="survey-question underline"
                      >
                        <p className="question-text">
                          {question.questionDescr} (Unsupported type:{" "}
                          {question.questionType})
                        </p>
                      </div>
                    );
                }
              })}
          </div>
        ))}

        <div className="survey-submit">
          <button
            className="submit-button"
            style={{
              backgroundColor: theme?.buttonColor, // backup
            }}
            disabled={submitting}
            onClick={handleSubmit}
          >
            Submit Survey
          </button>
        </div>
      </div>
    </>
  );
}
